import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { AgGridReact } from 'ag-grid-react'
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import '../../shared/ui/ag-grid-theme.css'
import type {
  ColDef,
  ColumnResizedEvent,
  DragStoppedEvent,
  SortChangedEvent,
} from 'ag-grid-community'
import type { SelectionChangedEvent } from 'ag-grid-community'
import { buildQueueColDefs } from './queueColDefs'
import type { QueueRow } from './queueTypes'
import { AG_GRID_LOCALE_RU } from '../../shared/ag-grid/locale'
import { refreshQueueNow } from '../../shared/realtime/queueRefresh'
import { useAuthStore } from '../auth/authStore'
import { useGridSelection } from '../../shared/hooks/useGridSelection'
import { useAgGridScrollRestoration } from '../../shared/hooks/useScrollRestoration'
import { PageLoader } from '../../shared/ui/Spinner'
import { EmptyState } from '../../shared/ui/EmptyState'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { CheckboxSetFilter } from '../table/CheckboxSetFilter'
import { SupplierSelectModal } from './components/SupplierSelectModal'
import { QueueCollapsibleSection } from './components/QueueCollapsibleSection'
import { QueueToolbar } from './components/QueueToolbar'
import { QueueHideBar } from './components/QueueHideBar'
import { QueueTruncatedNotice } from './components/QueueTruncatedNotice'
import { useQueueQuery } from './useQueueQuery'
import { useQueueColumnState } from './useQueueColumnState'
import { useQueueFilters } from './useQueueFilters'
import { useDashboardQueueLink } from './useDashboardQueueLink'
import { useQueueRows } from './useQueueRows'
import { useQueueUpload } from './useQueueUpload'
import { useQueueHide } from './useQueueHide'
import { useQueueSuppliers } from './useQueueSuppliers'
import { useQueueItemStatus } from './useQueueItemStatus'

ModuleRegistry.registerModules([AllCommunityModule])

/** Сама очередь. Гейт роли — в QueueRouteGuard.tsx (раздел закрыт для ADMIN). */
export function ProcurementQueuePage() {
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const [searchParams] = useSearchParams()

  // Предустановленные фильтры с KPI-карточек дашборда закупщика — preset из URL
  // overrides сохранённые фильтры. mine-new/mine-waiting фильтруют по
  // procurementState (не по assignedToMe).
  const preset = searchParams.get('filter')
  const {
    assignedToMe,
    setAssignedToMe,
    inQueue,
    setInQueue,
    overdue,
    setOverdue,
    burning,
    setBurning,
    stale,
    setStale,
    needsInfo,
    setNeedsInfo,
    rework,
    setRework,
    procStateFilter,
    setProcStateFilter,
    category,
    setCategory,
    priority,
    setPriority,
    searchText,
    setSearchText,
  } = useQueueFilters(preset)

  // Deep-link фильтры с дашборда РОЗ (procState/new/zeroPrice/wentToInvoice/
  // buyerId/requestType) — живут в URL, в localStorage не пишутся.
  const dash = useDashboardQueueLink()

  // Режим «Показать скрытые» (только ГД) — нужен до загрузки очереди, поэтому
  // держим здесь, а не в useQueueHide.
  const [showHidden, setShowHidden] = useState(false)

  // Загрузка очереди (подгрузка страниц + sentinel) и флаги первой страницы:
  // скрытие позиций (только ГД) и потолок неполной выборки (№319).
  const {
    items,
    isLoading,
    isError,
    error,
    isFetchingNextPage,
    sentinelRef,
    canHide,
    hiddenCount,
    truncatedLimit,
  } = useQueueQuery(
    { assignedToMe, overdue, burning, stale, needsInfo, rework, category, priority },
    dash.filters,
    showHidden,
  )

  const invalidateQueue = useCallback(() => refreshQueueNow(), [])

  // Смена статуса позиции / per-user state прямо из очереди (вынесено).
  const { onChangeStatus, onChangeProcState, onReleaseManufacturerClaim } =
    useQueueItemStatus(invalidateQueue)

  // Клиентские фильтры → QueueRow → секции «Новые / В работе / Обработано»
  // + счётчики совпадений поиска (вынесено в useQueueRows).
  const {
    rowData,
    activeRowData,
    inWorkRowData,
    processedRowData,
    hasSearch,
    activeMatches,
    inWorkMatches,
    processedMatches,
  } = useQueueRows({ items, inQueue, procStateFilter, searchText, userId: user?.id })
  const [inWorkExpanded, setInWorkExpanded] = useState(false)
  const [processedExpanded, setProcessedExpanded] = useState(false)

  // Автораскрытие блоков «В работе»/«Обработано» при поиске, если совпадение
  // нашлось там. Сворачивать автоматически не надо — пользователь сам решит.
  useEffect(() => {
    if (hasSearch && inWorkMatches > 0) setInWorkExpanded(true)
    if (hasSearch && processedMatches > 0) setProcessedExpanded(true)
  }, [hasSearch, inWorkMatches, processedMatches])

  // Режим «результат с дашборда» (16.07): отфильтрованные позиции часто целиком
  // лежат в нижних секциях (напр. «В счёт» — все в «Обработано») — раскрываем
  // секции сразу, чтобы результат не выглядел пустым.
  useEffect(() => {
    if (dash.active) {
      setInWorkExpanded(true)
      setProcessedExpanded(true)
    }
  }, [dash.active])

  const [selectedRows, setSelectedRows] = useState<QueueRow[]>([])
  const onSelectionChanged = useCallback((e: SelectionChangedEvent<QueueRow>) => {
    setSelectedRows(e.api.getSelectedRows())
  }, [])

  const { uploadRef, isUploading, uploadResult, handleUploadResponse } = useQueueUpload({
    items,
    queryClient,
    invalidateQueue,
  })

  // Колбэки в colDefs — стабильные ссылки из useQueueItemStatus (`.mutate` +
  // мемоизированный onReleaseManufacturerClaim): нестабильный колбэк пересоздаёт
  // columns и сбрасывает вид AG Grid (жалоба 04.05).
  const colDefs = useMemo<ColDef<QueueRow>[]>(
    () =>
      buildQueueColDefs({
        userId: user?.id,
        userRole: user?.role,
        onChangeStatus,
        onChangeProcState,
        onReleaseManufacturerClaim,
      }),
    [user?.id, user?.role, onChangeStatus, onChangeProcState, onReleaseManufacturerClaim],
  )

  const gridRef = useRef<AgGridReact<QueueRow>>(null)
  const inWorkGridRef = useRef<AgGridReact<QueueRow>>(null)
  const processedGridRef = useRef<AgGridReact<QueueRow>>(null)

  // Снятие выделения во всех трёх гридах — используется скрытием позиций.
  const deselectAllGrids = useCallback(() => {
    gridRef.current?.api?.deselectAll()
    inWorkGridRef.current?.api?.deselectAll()
    processedGridRef.current?.api?.deselectAll()
  }, [])

  // Мутации скрытия/возврата позиций (только ГД).
  const {
    hideSelected,
    unhideSelected,
    unhideAll,
    busy: hideBusy,
  } = useQueueHide({ selectedRows, invalidateQueue, deselectAll: deselectAllGrids })

  // Логика окна выбора поставщиков (вынесена из страницы).
  const {
    pendingRows,
    supplierModalOpen,
    setSupplierModalOpen,
    handleDownloadRequest,
    handleSectionSendRequest,
    handleConfirmSuppliers,
    handleDownloadNoSupplier,
  } = useQueueSuppliers({
    selectedRows,
    queryClient,
    invalidateQueue,
    deselectAll: deselectAllGrids,
  })

  const { onCellMouseDown, onCellMouseOver, getCellStyle } = useGridSelection(gridRef)
  // Восстановление скролла очереди при возврате со страницы позиции.
  const queueGridContainerRef = useRef<HTMLDivElement>(null)
  useAgGridScrollRestoration('procurement-grid', queueGridContainerRef)
  const {
    onCellMouseDown: onCellMouseDownInWork,
    onCellMouseOver: onCellMouseOverInWork,
    getCellStyle: getCellStyleInWork,
  } = useGridSelection(inWorkGridRef)
  const {
    onCellMouseDown: onCellMouseDownProcessed,
    onCellMouseOver: onCellMouseOverProcessed,
    getCellStyle: getCellStyleProcessed,
  } = useGridSelection(processedGridRef)

  // Общая форма defaultColDef; отличается только cellStyle своего useGridSelection.
  const mkDefaultColDef = (cellStyle: ColDef['cellStyle']): ColDef => ({
    sortable: true,
    resizable: true,
    suppressMovable: false,
    filter: CheckboxSetFilter,
    floatingFilter: false,
    cellStyle,
  })
  const defaultColDef = useMemo<ColDef>(
    () => mkDefaultColDef(getCellStyle),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getCellStyle],
  )
  const defaultColDefInWork = useMemo<ColDef>(
    () => mkDefaultColDef(getCellStyleInWork),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getCellStyleInWork],
  )
  const defaultColDefProcessed = useMemo<ColDef>(
    () => mkDefaultColDef(getCellStyleProcessed),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getCellStyleProcessed],
  )

  const { gridApiRef, sortActive, saveState, refreshSortActive, onGridReady, resetSort } =
    useQueueColumnState(user?.id)

  // Верхний блок (заголовок+фильтры) — sticky к верху main-контейнера.
  // Высота блока нужна контейнеру таблицы (sticky top = topBlockHeight),
  // чтобы заголовки колонок AG Grid держались при прокрутке всей страницы,
  // а не уезжали вверх вместе с таблицей.
  const topBlockRef = useRef<HTMLDivElement | null>(null)
  const [topBlockHeight, setTopBlockHeight] = useState(0)

  useEffect(() => {
    const el = topBlockRef.current
    if (!el) return
    const update = () => setTopBlockHeight(el.offsetHeight)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const anyFilterActive =
    assignedToMe ||
    inQueue ||
    overdue ||
    burning ||
    stale ||
    needsInfo ||
    rework ||
    category ||
    priority ||
    searchText ||
    procStateFilter !== null

  // Активный срез по городам: отфильтрованный список выглядит так же, как
  // полный, и человек не понимает, почему «часть позиций пропала». Пустой
  // список городов = «все города», бейдж не показываем.
  const cityScope = user?.cities ?? []

  return (
    <div className="space-y-3">
      {cityScope.length > 0 && (
        <div className="text-xs text-gray-500">
          Показаны позиции по городам:{' '}
          <span className="rounded-lg bg-gray-100 px-1.5 py-0.5 text-gray-700">
            {cityScope.join(', ')}
          </span>
        </div>
      )}
      <QueueToolbar
        topBlockRef={topBlockRef}
        activeCount={activeRowData.length}
        inWorkCount={inWorkRowData.length}
        processedCount={processedRowData.length}
        selectedCount={selectedRows.length}
        dash={dash}
        assignedToMe={assignedToMe}
        setAssignedToMe={setAssignedToMe}
        inQueue={inQueue}
        setInQueue={setInQueue}
        overdue={overdue}
        setOverdue={setOverdue}
        burning={burning}
        setBurning={setBurning}
        stale={stale}
        setStale={setStale}
        needsInfo={needsInfo}
        setNeedsInfo={setNeedsInfo}
        rework={rework}
        setRework={setRework}
        category={category}
        setCategory={setCategory}
        priority={priority}
        setPriority={setPriority}
        procStateFilter={procStateFilter}
        setProcStateFilter={setProcStateFilter}
        anyFilterActive={!!anyFilterActive}
        searchText={searchText}
        setSearchText={setSearchText}
        sortActive={sortActive}
        resetSort={resetSort}
        onDownloadRequest={() => void handleDownloadRequest()}
        onDeselectAll={() => gridApiRef.current?.deselectAll()}
        uploadRef={uploadRef}
        isUploading={isUploading}
        uploadResult={uploadResult}
        onUploadResponse={(e) => void handleUploadResponse(e)}
      />

      {/* Скрытие позиций у себя (только ГД) — «просмотрел и убрал из виду» */}
      <QueueHideBar
        canHide={canHide}
        hiddenCount={hiddenCount}
        showHidden={showHidden}
        selectedCount={selectedRows.length}
        busy={hideBusy}
        onHideSelected={hideSelected}
        onUnhideSelected={unhideSelected}
        onUnhideAll={unhideAll}
        onToggleShowHidden={setShowHidden}
      />

      {/* Страховка №319: выборка упёрлась в потолок — показаны не все позиции */}
      {truncatedLimit !== null && <QueueTruncatedNotice limit={truncatedLimit} />}

      {/* Таблица активных позиций */}
      {isLoading ? (
        <PageLoader />
      ) : isError ? (
        // Иначе сбой загрузки очереди читается как «работы нет».
        <ErrorMessage error={error} />
      ) : !rowData.length ? (
        <EmptyState
          title="Нет позиций для квотирования"
          description="По текущим фильтрам работы нет"
        />
      ) : (
        <>
          {/* «Нет активных позиций» показываем только когда нет поиска —
             при поиске активная таблица скрывается без подписи, чтобы не было
             пустого пространства между блоками. */}
          {!hasSearch && activeRowData.length === 0 && (
            <EmptyState
              title="Нет активных позиций"
              description="Все позиции в этой очереди уже обработаны — см. раздел ниже"
            />
          )}
          {/* Поиск не дал ни одного совпадения нигде — единственный случай,
             когда показываем «Ничего не найдено». Если совпало в «В работе» /
             «Обработано» — блок раскроется ниже автоматически, заглушка не нужна. */}
          {hasSearch && activeMatches === 0 && inWorkMatches === 0 && processedMatches === 0 && (
            <EmptyState
              title="Ничего не найдено"
              description="Уточните поисковый запрос или сбросьте фильтры"
            />
          )}
          {activeRowData.length > 0 && (!hasSearch || activeMatches > 0) && (
            // При поиске — `domLayout="autoHeight"`: AG Grid сам считает высоту по
            // реальному контенту (в т.ч. wrap'нутым строкам с длинной заметкой).
            // Без поиска — стандартная фикс-высота `calc(100vh - 320px)` со
            // внутренним скроллом, чтобы листать длинные списки.
            // .ag-header прилипает к top=topBlockHeight через crm-sticky-header.
            <div
              ref={queueGridContainerRef}
              className={`crm-table crm-sticky-header ag-theme-alpine rounded-xl border border-gray-200 bg-white shadow-sm ${
                hasSearch ? '' : 'h-[calc(100vh-320px)] min-h-[420px]'
              }`}
              style={{ ['--crm-sticky-top' as string]: `${topBlockHeight}px` }}
            >
              <AgGridReact<QueueRow>
                ref={gridRef}
                getRowId={({ data }) => data.id}
                rowData={activeRowData}
                columnDefs={colDefs}
                defaultColDef={defaultColDef}
                localeText={AG_GRID_LOCALE_RU}
                quickFilterText={searchText}
                domLayout={hasSearch ? 'autoHeight' : 'normal'}
                rowSelection={{
                  mode: 'multiRow',
                  checkboxes: true,
                  headerCheckbox: true,
                  selectAll: 'filtered',
                  enableClickSelection: false,
                }}
                onSelectionChanged={onSelectionChanged}
                onGridReady={onGridReady}
                onCellMouseDown={onCellMouseDown}
                onCellMouseOver={onCellMouseOver}
                onDragStopped={(e: DragStoppedEvent) => saveState(e.api)}
                onColumnResized={(e: ColumnResizedEvent) => {
                  if (e.finished) saveState(e.api)
                }}
                onSortChanged={(e: SortChangedEvent) => {
                  saveState(e.api)
                  refreshSortActive(e.api)
                }}
                rowHeight={44}
                headerHeight={40}
                suppressDragLeaveHidesColumns
                suppressCellFocus
              />
            </div>
          )}

          <QueueCollapsibleSection
            title="В работе"
            hint="Запрос поставщику отправлен, ответа ещё нет — можно выбрать и отправить другому"
            rows={inWorkRowData}
            colDefs={colDefs}
            defaultColDef={defaultColDefInWork}
            expanded={inWorkExpanded}
            onToggle={() => setInWorkExpanded((v) => !v)}
            hasSearch={hasSearch}
            searchText={searchText}
            gridRef={inWorkGridRef}
            onCellMouseDown={onCellMouseDownInWork}
            onCellMouseOver={onCellMouseOverInWork}
            selectable
            onSendRequest={handleSectionSendRequest}
          />

          <QueueCollapsibleSection
            title="Обработано"
            hint="Есть цена (в т.ч. 0) или финальный статус — нулевые можно переотправить"
            rows={processedRowData}
            colDefs={colDefs}
            defaultColDef={defaultColDefProcessed}
            expanded={processedExpanded}
            onToggle={() => setProcessedExpanded((v) => !v)}
            hasSearch={hasSearch}
            searchText={searchText}
            gridRef={processedGridRef}
            onCellMouseDown={onCellMouseDownProcessed}
            onCellMouseOver={onCellMouseOverProcessed}
            selectable
            onSendRequest={handleSectionSendRequest}
            showZeroFilter
          />
        </>
      )}

      {/* Sentinel для IntersectionObserver — триггер догрузки следующей страницы */}
      <div ref={sentinelRef} className="h-4" />
      {isFetchingNextPage && (
        <div className="py-3 flex justify-center text-sm text-gray-500">
          Загрузка следующей страницы…
        </div>
      )}

      {supplierModalOpen && (
        <SupplierSelectModal
          itemsCount={pendingRows.length}
          onClose={() => setSupplierModalOpen(false)}
          onConfirm={handleConfirmSuppliers}
          onDownloadNoSupplier={handleDownloadNoSupplier}
        />
      )}
    </div>
  )
}
